import { Flex } from '@momentum-ui/react-collaboration';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import useWebexClasses from '../../hooks/useWebexClasses';
import './CallHistory.styles.scss';
import { CallHistoryItem } from './CallHistoryItem';
import { ICallHistoryItemProps } from './CallHistoryItem.types';
import { CallHistoryExpandedRow } from './AISummary/CallHistoryExpandedRow';
import { RecapTab } from './AISummary/CallRecapModal';
import './AISummary/AISummary.styles.scss';

export type CallHistoryProps = {
  items: ICallHistoryItemProps[];
  testMode?: boolean;
  style?: React.CSSProperties;
  onPress?: (item: ICallHistoryItemProps) => void;
  selectedItem?: ICallHistoryItemProps;
  extraCallHistoryItemProps?: ICallHistoryItemProps;
  makeCall?: (address: string, isVideo?: boolean, label?: string) => void;
  isLocaleGerman?: boolean;
  dismissBagdeonClickRow?: () => void;
  labelUnknown?: string;
  deleteSingleCallHistoryRecord?: (id: string, endTime: string) => void;
  isActive?: boolean;
  onOpenRecapModal?: (item: ICallHistoryItemProps, tab: RecapTab) => void;
  onVisibleItemsChange?: (visibleIndices: number[]) => void;
};

const COLLAPSED_HEIGHT = 50;
const EXPANDED_HEIGHT = 230;

  // ------------------ Row Rendering (memoized like VoicemailList) ------------------
  interface RowDataShape {
    items: ICallHistoryItemProps[];
    onItemPress?: (item: ICallHistoryItemProps) => void;
    selectedId?: string;
    extra?: ICallHistoryItemProps;
    makeCall?: (address: string, isVideo?: boolean, label?: string) => void;
    isLocaleGerman?: boolean;
    dismissBadge?: () => void;
    labelUnknown?: string;
    deleteSingle?: (id: string, endTime: string) => void;
    expandedId?: string | null;
    onExpandToggle?: (id: string) => void;
    onOpenRecapModal?: (item: ICallHistoryItemProps, tab: RecapTab) => void;
    onVisibleItemsChange?: (visibleIndices: number[]) => void;
  }

  const CallHistoryRow: React.FC<ListChildComponentProps<RowDataShape>> = React.memo(({ index, style: rowStyle, data }) => {
    const item = data.items[index];
    // Defensively guard (react-window might call for an index while length updating)
    if (!item) return null;

    const hasAISummary = Boolean(item.callingContainerIds?.length);
    const isExpanded = hasAISummary && data.expandedId === item.id;
    const expandedRowId = hasAISummary ? `call-history-ai-summary-${item.id}` : undefined;

    const handlePress = () => {
      // If item has AI summary, toggle expand instead of just selecting
      if (hasAISummary && data.onExpandToggle) {
        data.onExpandToggle(item.id);
      }
      data.onItemPress?.(item);
      data.dismissBadge?.();
    };

    return (
      <div style={{ ...rowStyle, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '12px', background: 'var(--mds-color-theme-background-primary-normal)' }}>
        <div style={{ height: COLLAPSED_HEIGHT, flexShrink: 0 }}>
          <CallHistoryItem
            audioCallLabel={data.extra?.audioCallLabel}
            videoCallLabel={data.extra?.videoCallLabel}
            itemIndex={index}
            onPress={handlePress}
              /* All props forwarded */
            id={item.id}
            name={item.name}
            isSelected={data.selectedId === item.id}
            phoneNumber={item.phoneNumber}
            direction={item.direction}
            disposition={item.disposition}
            startTime={item.startTime}
            endTime={item.endTime}
            callbackAddress={item.callbackAddress}
            durationSeconds={item.durationSeconds}
            makeCall={data.makeCall}
            callingSpecific={item.callingSpecific}
            isLocaleGerman={data.isLocaleGerman}
            dismissBagdeonClickRow={data.dismissBadge}
            lineNumber={item.lineNumber}
            managedByName={item.managedByName}
            labelUnknown={data.labelUnknown}
            deleteSingleCallHistoryRecord={data.deleteSingle}
            callingContainerIds={item.callingContainerIds}
            isExpanded={isExpanded}
            onExpandToggle={data.onExpandToggle}
            expandedRowId={expandedRowId}
            presenceType={item.presenceType}
            presenceLabel={item.presenceLabel}
            remotePartyId={item.remotePartyId}
          />
        </div>
        {isExpanded && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <CallHistoryExpandedRow
              expandedRowId={expandedRowId}
              containerId={item.callingContainerIds![0]}
              callerName={item.name}
              direction={item.direction || ''}
              startTime={item.startTime}
              durationSeconds={item.durationSeconds || 0}
              onCallSummaryClick={() => data.onOpenRecapModal?.(item, 'summary')}
              onTranscriptClick={() => data.onOpenRecapModal?.(item, 'transcript')}
            />
          </div>
        )}
      </div>
    );
  });
  CallHistoryRow.displayName = 'CallHistoryRow';

export const CallHistory = ({
  items,
  style = undefined,
  onPress = undefined,
  selectedItem = undefined,
  extraCallHistoryItemProps = undefined,
  makeCall,
  isLocaleGerman,
  dismissBagdeonClickRow,
  labelUnknown,
  deleteSingleCallHistoryRecord,
  isActive,
  testMode = false,
  onOpenRecapModal,
  onVisibleItemsChange
}: CallHistoryProps) => {
  const [cssClasses, sc] = useWebexClasses('call-history');
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<VariableSizeList>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const makeCallStable = useCallback((address: string, isVideo?: boolean, label?: string) => {
    makeCall && makeCall(address, isVideo, label);
  }, [makeCall]);

  // Stable callback for row press combining parent handlers
  const handleItemPress = useCallback((item: ICallHistoryItemProps) => {
    onPress?.(item);
  }, [onPress]);

  const handleExpandToggle = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  // Reset VariableSizeList cached sizes whenever expandedId changes
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [expandedId]);

  const getItemSize = useCallback((index: number) => {
    const item = items[index];
    if (item?.id === expandedId && item?.callingContainerIds?.length) {
      return EXPANDED_HEIGHT;
    }
    return COLLAPSED_HEIGHT;
  }, [items, expandedId]);

  // Build stable itemData object passed to virtualized list
  const itemData: RowDataShape = useMemo(() => ({
    items,
    onItemPress: handleItemPress,
    selectedId: selectedItem?.id,
    extra: extraCallHistoryItemProps,
    makeCall: makeCallStable,
    isLocaleGerman,
    dismissBadge: dismissBagdeonClickRow,
    labelUnknown,
    deleteSingle: deleteSingleCallHistoryRecord,
    expandedId,
    onExpandToggle: handleExpandToggle,
    onOpenRecapModal,
    onVisibleItemsChange,
  }), [items, handleItemPress, selectedItem?.id, extraCallHistoryItemProps, makeCall, isLocaleGerman, dismissBagdeonClickRow, labelUnknown, deleteSingleCallHistoryRecord, expandedId, handleExpandToggle, onOpenRecapModal, onVisibleItemsChange]);

  // Height measurement simplified: observe the immediate container's parent to sync height exactly and avoid extra scrollbar.
  useEffect(() => {
    if (typeof isActive === 'boolean' && !isActive) return;
    const target = containerRef.current; // our own container
    if (!target) return;
    const parent = target.parentElement as HTMLElement | null;
    if (!parent) return;
    // Ensure outer container doesn't scroll; only virtual list should.
    parent.style.overflow = 'hidden';
    const TOLERANCE = 2;
    let raf: number | null = null;
    const measure = () => {
      const h = Math.floor(parent.getBoundingClientRect().height);
      setContainerHeight(prev => (typeof prev !== 'number' || Math.abs(prev - h) > TOLERANCE ? h : prev));
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(measure);
      });
      ro.observe(parent);
      window.addEventListener('resize', measure);
      return () => {
        if (raf) cancelAnimationFrame(raf);
        ro.disconnect();
        window.removeEventListener('resize', measure);
      };
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isActive]);

  if (testMode) {
    // Non-virtual rendering for deterministic tests
    return (
      <div data-testid="call-history-list" style={{ height: '100%', flex: '1 1 auto', minHeight: 0, overflow: 'hidden', ...style }}>
        <Flex direction="column" className={cssClasses} style={{ height: '100%', minHeight: 0 }}>
          {items.map((it, index) => {
            const hasAISummary = Boolean(it.callingContainerIds?.length);
            const expandedRowId = hasAISummary ? `call-history-ai-summary-${it.id}` : undefined;
            const handlePress = () => {
              if (hasAISummary) {
                handleExpandToggle(it.id);
              }
              handleItemPress(it);
              dismissBagdeonClickRow?.();
            };

            return (
              <div key={it.id || index}>
              <CallHistoryItem
                audioCallLabel={extraCallHistoryItemProps?.audioCallLabel}
                videoCallLabel={extraCallHistoryItemProps?.videoCallLabel}
                itemIndex={index}
                onPress={handlePress}
                id={it.id}
                name={it.name}
                isSelected={selectedItem?.id === it.id}
                phoneNumber={it.phoneNumber}
                direction={it.direction}
                disposition={it.disposition}
                startTime={it.startTime}
                endTime={it.endTime}
                callbackAddress={it.callbackAddress}
                durationSeconds={it.durationSeconds}
                makeCall={makeCallStable}
                callingSpecific={it.callingSpecific}
                isLocaleGerman={isLocaleGerman}
                dismissBagdeonClickRow={dismissBagdeonClickRow}
                lineNumber={it.lineNumber}
                managedByName={it.managedByName}
                labelUnknown={labelUnknown}
                deleteSingleCallHistoryRecord={deleteSingleCallHistoryRecord}
                callingContainerIds={it.callingContainerIds}
                isExpanded={expandedId === it.id && Boolean(it.callingContainerIds?.length)}
                onExpandToggle={handleExpandToggle}
                expandedRowId={expandedRowId}
                presenceType={it.presenceType}
                presenceLabel={it.presenceLabel}
                remotePartyId={it.remotePartyId}
              />
              {expandedId === it.id && it.callingContainerIds?.length && (
                <CallHistoryExpandedRow
                  expandedRowId={expandedRowId}
                  containerId={it.callingContainerIds[0]}
                  callerName={it.name}
                  direction={it.direction || ''}
                  startTime={it.startTime}
                  durationSeconds={it.durationSeconds || 0}
                  onCallSummaryClick={() => onOpenRecapModal?.(it, 'summary')}
                  onTranscriptClick={() => onOpenRecapModal?.(it, 'transcript')}
                />
              )}
              </div>
            );
          })}
        </Flex>
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      data-testid="call-history-list"
      style={{
        height: '100%',
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden',
        ...style,
      }}
    >
      <Flex
        direction="column"
        className={cssClasses}
        style={{
          height: '100%',
          minHeight: 0,
        }}
      >
        {isActive && items && containerHeight && containerHeight > 0 && (
          <div role="list" aria-label="Recent call list" style={{ width: '100%', height: containerHeight }}>
            <VariableSizeList
              ref={listRef}
              height={containerHeight}
              width={'100%'}
              itemCount={items.length}
              itemSize={getItemSize}
              className={sc('list')}
              overscanCount={4}
              style={{ width: '100%', height: containerHeight }}
              itemData={itemData}
              itemKey={(index, data) => {
                const item = data.items[index];
                return item?.id ?? String(index);
              }}
              onItemsRendered={onVisibleItemsChange ? ({ visibleStartIndex, visibleStopIndex }) => {
                // Extract visible indices (including overscan)
                const visibleIndices: number[] = [];
                for (let i = visibleStartIndex; i <= visibleStopIndex; i++) {
                  visibleIndices.push(i);
                }
                onVisibleItemsChange(visibleIndices);
              } : undefined}
            >
              {CallHistoryRow as any}
            </VariableSizeList>
          </div>
        )}
      </Flex>
    </div>
  );
};
